import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProfessionalCombobox } from "@/components/common/professional-combobox";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { useProducts } from "@/features/forms/hooks/use-products";
import { Product } from "@/pages/forms/types/product";
import { useEffect } from "react";

interface SessionFiltersProps {
  usage_id?: string;
}

export function SessionFilters({ usage_id }: SessionFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current filter values from URL
  const dateRangeStart = searchParams.get("start_date");
  const dateRangeEnd = searchParams.get("end_date");
  const status = searchParams.get("status") || "all";
  const tag = searchParams.get("tag") || "all";
  const professionalId = searchParams.get("professional_id") || "ALL";
  const productId = searchParams.get("product_id") || "ALL";

  const { data: professionalsData, isLoading: professionalsLoading } =
    useAllProfessionals();

  const { data: products, isLoading: productsLoading } = useProducts();

  const productOptions = !productsLoading && products
    ? [
        { value: "ALL", label: "All Categories" },
        ...products.map((product: Product) => ({
          value: product.id,
          label: product.name,
        })),
      ]
    : [{ value: "ALL", label: "All Categories" }];

  const form = useForm({
    defaultValues: {
      dateRange: {
        start: dateRangeStart ? new Date(dateRangeStart) : null,
        end: dateRangeEnd ? new Date(dateRangeEnd) : null,
      },
      status,
      tag,
      professionalId,
      productId,
    },
  });

  // Update URL params when filters change
  const handleFilterChange = (updates: Record<string, any>) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0");

    Object.entries(updates).forEach(([key, value]) => {
      if (key === "dateRange") {
        // Only update URL if both dates are selected
        if (value.start && value.end) {
          newParams.set("start_date", value.start.toISOString());
          // Set end_date to EOD (23:59:59) in ISO string
          const endDate = new Date(value.end);
          endDate.setHours(23, 59, 59, 999);
          newParams.set("end_date", endDate.toISOString());
        } else {
          // Clear dates if not both selected
          newParams.delete("start_date");
          newParams.delete("end_date");
        }
      } else if (value && value !== "ALL" && value !== "all") {
        newParams.set(key === "professionalId" ? "professional_id" : key === "productId" ? "product_id" : key, value);
      } else {
        newParams.delete(key === "professionalId" ? "professional_id" : key === "productId" ? "product_id" : key);
      }
    });

    if (usage_id) newParams.set("usage_id", usage_id);
    setSearchParams(newParams);
  };

  // Replaces the old handleReset()/hasFilters pair that used to live here.
  // handleReset was dead code (no button ever called it) and it coupled two
  // jobs: rewriting the URL AND manually form.reset()-ing to hardcoded
  // defaults. That design breaks whenever the URL changes from anywhere else
  // (back/forward navigation, a pasted link, the page-level Clear filters
  // button) — the form was only initialized on mount, so the dropdowns kept
  // showing stale values. The URL rewrite moved to handleClearFilters in
  // pages/sessions/sessions.tsx (where the button renders); this effect keeps
  // the form mirroring the URL params, whoever changed them.
  useEffect(() => {
    // Changes made through this form rewrite the same params, so the effect
    // fires for them too — skip the reset when the form already matches the
    // URL, otherwise every filter interaction would discard RHF dirty/touched
    // state and rebuild dateRange for no visible change.
    const current = form.getValues();
    const dateMatches = (formDate: Date | null, isoParam: string | null) =>
      formDate && isoParam
        ? formDate.getTime() === new Date(isoParam).getTime()
        : !formDate && !isoParam;
    if (
      dateMatches(current.dateRange?.start, dateRangeStart) &&
      dateMatches(current.dateRange?.end, dateRangeEnd) &&
      current.status === status &&
      current.tag === tag &&
      current.professionalId === professionalId &&
      current.productId === productId
    ) {
      return;
    }

    form.reset({
      dateRange: {
        start: dateRangeStart ? new Date(dateRangeStart) : null,
        end: dateRangeEnd ? new Date(dateRangeEnd) : null,
      },
      status,
      tag,
      professionalId,
      productId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `form` is a stable ref from useForm, deliberately omitted
  }, [dateRangeStart, dateRangeEnd, status, tag, professionalId, productId]);

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range */}
        <FormField
          control={form.control}
          name="dateRange"
          render={({ field }) => (
            <FormItem>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={`w-[240px] justify-start text-left font-normal bg-white text-gray-900 border-gray-300 hover:bg-gray-50 overflow-hidden ${
                        !field.value.start && "text-gray-500"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {field.value.start && field.value.end ? (
                          <>
                            {format(field.value.start, "MMM dd, yy")} -{" "}
                            {format(field.value.end, "MMM dd, yy")}
                          </>
                        ) : (
                          "Pick a Date Range"
                        )}
                      </span>
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Two input boxes for start and end dates */}
                  <div className="flex items-end gap-3 mb-4 px-4">
                    {/* Start Date Box */}
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        Start
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.start ? (
                          format(field.value.start, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />

                    {/* End Date Box */}
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        End
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.end ? (
                          format(field.value.end, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>

                    {/* Clear button - X icon after end box */}
                    {(field.value.start || field.value.end) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 mb-0 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const newValue = {
                            start: null,
                            end: null,
                          };
                          field.onChange(newValue);
                          handleFilterChange({ dateRange: newValue });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Calendar below the boxes */}
                  <Calendar
                    initialFocus
                    mode="range"
                    selected={{
                      from: field.value.start || undefined,
                      to: field.value.end || undefined,
                    }}
                    onSelect={(range) => {
                      const newValue = {
                        start: range?.from || null,
                        end: range?.to || null,
                      };
                      field.onChange(newValue);
                      // Only trigger API call if both dates are selected
                      if (range?.from && range?.to) {
                        handleFilterChange({ dateRange: newValue });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </FormItem>
          )}
        />

        {/* Status */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleFilterChange({ status: value });
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-[140px] bg-white text-gray-900 border-gray-300">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {/* Overlay tags (independent of status) */}
        <FormField
          control={form.control}
          name="tag"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleFilterChange({ tag: value });
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-[150px] bg-white text-gray-900 border-gray-300">
                    <SelectValue placeholder="All Tag" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="all">All Tag</SelectItem>
                  <SelectItem value="unsuccessful">Unsuccessful</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {/* Professional — shared searchable combobox (see professional-combobox.tsx
            for why it's a Popover rather than a Radix Select) */}
        <FormField
          control={form.control}
          name="professionalId"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[200px]">
              <ProfessionalCombobox
                professionals={professionalsData?.professionals || []}
                value={field.value}
                disabled={professionalsLoading}
                onSelect={(value) => {
                  field.onChange(value);
                  handleFilterChange({ professionalId: value });
                }}
              />
            </FormItem>
          )}
        />

        {/* Product */}
        <FormField
          control={form.control}
          name="productId"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleFilterChange({ productId: value });
                }}
                disabled={productsLoading}
              >
                <FormControl>
                  <SelectTrigger className="w-[160px] bg-white text-gray-900 border-gray-300">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {productOptions.map((product) => (
                    <SelectItem key={product.value} value={product.value}>
                      {product.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      </div>
    </Form>
  );
}

